import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useRouteInfo } from './useRouteInfo';
import RouteInfoHeader from './RouteInfoHeader';
import RouteInfoStats from './RouteInfoStats';
import RouteInfoWeather from './RouteInfoWeather';
import RouteInfoDeviation from './RouteInfoDeviation';
import './routeInfo.styles.css';

/**
 * Main route information panel component
 * @param {Object} props
 * @param {string | null} props.routeId - Route ID to display
 * @param {Object | null} props.selectedDatapoint - Selected datapoint information
 * @param {() => void} props.onClose - Callback when panel is closed
 */
const RouteInfoPanel = ({ routeId, selectedDatapoint, onClose }) => {
  const { data, loading, error } = useRouteInfo(routeId);
  const [isDatapointExpanded, setIsDatapointExpanded] = useState(true);

  // Don't render if no route is selected
  if (!routeId) {
    return null;
  }

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return String(timestamp);
    }
  };

  /**
   * Format time only
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return String(timestamp);
    }
  };

  /**
   * Format heading/course
   */
  const formatHeading = (degrees) => {
    if (degrees === null || degrees === undefined) return 'N/A';
    const heading = parseFloat(degrees);
    if (isNaN(heading)) return 'N/A';
    
    // Convert to compass direction
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 
                       'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(heading / 22.5) % 16;
    
    return `${heading.toFixed(1)}° (${directions[index]})`;
  };

  return (
    <div className={`route-info-panel ${routeId ? 'open' : ''}`}>
      <div className="route-info-content">
        {/* Close Button */}
        <button 
          className="route-info-close" 
          onClick={onClose}
          aria-label="Close route info"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path 
              d="M15 5L5 15M5 5L15 15" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Loading State */}
        {loading && (
          <div className="route-info-loading">
            <div className="loading-spinner"></div>
            <p>Loading route information...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="route-info-error">
            <p className="error-message">⚠️ {error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        )}

        {/* Data Display */}
        {data && !loading && !error && (
          <>
            <RouteInfoHeader
              vesselName={data.vesselName}
              date={data.date}
              startTime={data.startTime}
              endTime={data.endTime}
            />

            <RouteInfoStats
              distanceNm={data.distanceNm}
              durationMinutes={data.durationMinutes}
              avgSpeed={data.avgSpeed}
              maxSpeed={data.maxSpeed}
            />

            <RouteInfoWeather weatherSummary={data.weatherSummary} />

            <RouteInfoDeviation deviationScore={data.deviationScore} />
          </>
        )}

        {/* Datapoint Information - Collapsible at the bottom */}
        {selectedDatapoint && (
          <div className="route-info-datapoint route-info-datapoint-bottom">
            <div 
              className="datapoint-header"
              onClick={() => setIsDatapointExpanded(!isDatapointExpanded)}
            >
              <h3 className="datapoint-title">
                Datapoint {selectedDatapoint.index + 1} of {selectedDatapoint.totalPoints}
              </h3>
              <button 
                className="datapoint-toggle"
                aria-label={isDatapointExpanded ? 'Collapse datapoint' : 'Expand datapoint'}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="none"
                  style={{ 
                    transform: isDatapointExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path 
                    d="M4 6L8 10L12 6" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {isDatapointExpanded && (
              <div className="datapoint-details">
                {/* Time */}
                {selectedDatapoint.timestamp && (
                  <div className="datapoint-field">
                    <span className="field-label">Time:</span>
                    <span className="field-value">
                      {formatTime(selectedDatapoint.timestamp)}
                    </span>
                  </div>
                )}
                
                {/* Position */}
                <div className="datapoint-field">
                  <span className="field-label">Latitude:</span>
                  <span className="field-value">
                    {selectedDatapoint.coordinates.latitude.toFixed(6)}°
                  </span>
                </div>
                <div className="datapoint-field">
                  <span className="field-label">Longitude:</span>
                  <span className="field-value">
                    {selectedDatapoint.coordinates.longitude.toFixed(6)}°
                  </span>
                </div>
                
                {/* Speed */}
                {selectedDatapoint.speed !== null && selectedDatapoint.speed !== undefined && (
                  <div className="datapoint-field">
                    <span className="field-label">Speed (SOG):</span>
                    <span className="field-value">
                      {selectedDatapoint.speed.toFixed(1)} knots
                    </span>
                  </div>
                )}
                
                {/* Course Over Ground */}
                {selectedDatapoint.cog !== null && selectedDatapoint.cog !== undefined && (
                  <div className="datapoint-field">
                    <span className="field-label">Course (COG):</span>
                    <span className="field-value">
                      {formatHeading(selectedDatapoint.cog)}
                    </span>
                  </div>
                )}
                
                {/* Heading */}
                {selectedDatapoint.heading !== null && selectedDatapoint.heading !== undefined && (
                  <div className="datapoint-field">
                    <span className="field-label">Heading:</span>
                    <span className="field-value">
                      {formatHeading(selectedDatapoint.heading)}
                    </span>
                  </div>
                )}

                {/* Depth */}
                {selectedDatapoint.depth !== null && selectedDatapoint.depth !== undefined && (
                  <div className="datapoint-field">
                    <span className="field-label">Depth:</span>
                    <span className="field-value">
                      {selectedDatapoint.depth.toFixed(1)} m
                    </span>
                  </div>
                )}
                
                {/* Route Type */}
                <div className="datapoint-field">
                  <span className="field-label">Route Type:</span>
                  <span className="field-value">
                    {selectedDatapoint.routeType === 'sop' ? 'SOP Route' : 'Vessel Run'}
                  </span>
                </div>
                
                {/* Full Timestamp */}
                {selectedDatapoint.timestamp && (
                  <div className="datapoint-field datapoint-field-full">
                    <span className="field-label">Full Timestamp:</span>
                    <span className="field-value field-value-small">
                      {formatTimestamp(selectedDatapoint.timestamp)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

RouteInfoPanel.propTypes = {
  routeId: PropTypes.string,
  selectedDatapoint: PropTypes.shape({
    index: PropTypes.number.isRequired,
    coordinates: PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired
    }).isRequired,
    timestamp: PropTypes.any,
    speed: PropTypes.number,
    cog: PropTypes.number,
    heading: PropTypes.number,
    depth: PropTypes.number,
    routeId: PropTypes.any,
    routeType: PropTypes.string.isRequired,
    totalPoints: PropTypes.number.isRequired
  }),
  onClose: PropTypes.func.isRequired,
};

export default RouteInfoPanel;